
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';

interface RecipeErrorReportFormProps {
  recipeId: string;
  recipeName: string;
}

const RecipeErrorReportForm: React.FC<RecipeErrorReportFormProps> = ({ 
  recipeId,
  recipeName
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  const form = useForm({
    defaultValues: {
      errorDescription: ''
    }
  });

  const handleSubmit = async (values: { errorDescription: string }) => {
    if (!user) {
      toast.error('Debes iniciar sesión para reportar un error');
      return;
    }

    if (!values.errorDescription.trim()) {
      toast.error('Por favor, describe el error encontrado');
      return;
    }

    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('recipe_error_reports')
        .insert({
          user_id: user.id,
          recipe_id: recipeId,
          error_description: values.errorDescription
        });

      if (error) {
        console.error('Error al enviar reporte:', error);
        toast.error('No se pudo enviar el reporte. Inténtalo de nuevo.');
        return;
      }

      toast.success('Reporte enviado correctamente. ¡Gracias por tu ayuda!');
      form.reset();
      setIsOpen(false);
    } catch (err) {
      console.error('Error inesperado:', err);
      toast.error('Ocurrió un error al enviar el reporte');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <span
        className="absolute bottom-0 right-0 cursor-pointer flex items-center justify-center bg-white bg-opacity-80 text-xs text-gray-700 hover:text-gray-900 px-2 py-1 rounded-tl-md shadow-sm"
        onClick={() => setIsOpen(true)}
        title="Reportar error"
      >
        <AlertCircle className="h-4 w-4 mr-1" />
        ¿Hay un error en la receta?
      </span>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reportar error en la receta</DialogTitle>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="text-sm mb-4">
                <p className="font-medium text-gray-700">Receta: {recipeName}</p>
                <p className="text-gray-600 text-xs">ID: {recipeId}</p>
              </div>
              
              <FormField
                control={form.control}
                name="errorDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Describe el error o problema encontrado</FormLabel>
                    <Textarea 
                      {...field}
                      placeholder="Por ejemplo: Las cantidades de los ingredientes no son correctas, falta un paso en las instrucciones..."
                      className="min-h-[120px]"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar reporte'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RecipeErrorReportForm;
